import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { newsfeedApi } from "../api/newsFeedApi";
import PostItem from "../components/newsfeed/PostItem";
import ComposePost from "../components/newsfeed/ComposePost";
import SendLumensModal from "../components/newsfeed/SendLumensModal";
import ContestSwitcher from "../components/newsfeed/ContestSwitcher";
import PostTypeFilterSidebar from "../components/newsfeed/PostTypeFilterSidebar";
import NewsfeedFilters from "../components/newsfeed/NewsfeedFilters";
import MobilePostTypeFilters from "../components/newsfeed/MobilePostTypeFilters";
import PostSkeleton from "../components/newsfeed/PostSkeleton";
import useUserStore from "../zustand/UseUserStore";
import { useNavigate, useParams } from "react-router-dom";
import useNewsfeedStore from "../zustand/UseNewsfeed";
import { userAuthAPI } from "../api/userAuthAPI";

import { ArrowUp, Heart, Award, Star, Plus } from "lucide-react";
import { useInviteAcceptance } from "../hooks/useInviteAcceptance";
import InviteConfirmationModal from "../components/invites/InviteConfirmationModal";
import { Toaster } from "sonner";
import { getPostId, getPostUniqueId } from "../utils/postTypeUtils";
import usePostTypeFilters from "../hooks/usePostTypeFilters";
import useContestUtils from "../hooks/useContestUtils";
import useInfiniteScroll from "../hooks/useInfiniteScroll";

const Newsfeed = ({ showPost = false }) => {
	const { id, postType: urlPostType } = useParams();
	const navigate = useNavigate();

	const newsfeed = useNewsfeedStore();
	const userStore = useUserStore();

	// Use the invite acceptance hook
	const {
		inviteData,
		isProcessing,
		showModal,
		error,
		isInviteURL,
		setShowModal,
	} = useInviteAcceptance();

	// Only show modal if we're actually in an invite flow
	const shouldShowInviteModal = showModal && inviteData && isInviteURL;

	// Use contest utilities hook
	const {
		contests,
		getInitialContestIDLocal,
		getContestIndexFromIDLocal,
		getContestIDFromIndexLocal,
	} = useContestUtils(userStore);

	const sortOptions = ["Newest", "Oldest", "Most Likes", "Most Lumens"];
	const defaultFilters = {
		sort: 0,
		contestid: 0,
	};

	// Post types configuration
	const postTypes = [
		{
			id: "glow",
			label: "Glow",
			icon: Star,
			color: "bg-yellow-500",
			lumens: "+ 1-10",
			description: "Nice act you did for others",
		},
		{
			id: "pride",
			label: "Proud",
			icon: Award,
			color: "bg-purple-500",
			lumens: "1-5",
			description: "Something you accomplished",
		},
		{
			id: "grateful",
			label: "Grateful",
			icon: Heart,
			color: "bg-green-500",
			lumens: "- 2",
			description: "Someone did something nice for you",
		},
	];

	// Local state
	const [selectedPost, setSelectedPost] = useState(null);
	const [loading, setLoading] = useState(true);
	const topOfFeedRef = useRef(null);
	const [filters, setFilters] = useState(() => ({
		...defaultFilters,
		contestid: getInitialContestIDLocal(),
	}));
	const [selectedPostType, setSelectedPostType] = useState("glow");
	const [showComments, setShowComments] = useState(true);
	const [showCreatePost, setShowCreatePost] = useState(false);
	const [selectedCommentsPost, setSelectedCommentsPost] = useState(null);
	const [showSendLumens, setShowSendLumens] = useState(false);
	const [selectedPostForLumens, setSelectedPostForLumens] = useState(null);
	const [lumensToSend] = useState(5);

	// Use post type filters hook
	const {
		postTypeFilters,
		hasActiveFilters,
		filteredPosts,
		togglePostTypeFilter,
		clearPostTypeFilters,
	} = usePostTypeFilters(newsfeed.posts || []);

	// Handle URL parameters for contest selection and modal opening
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const contestNameParam = params.get("contestName");
		const openComposeParam = params.get("openCompose");

		if (contestNameParam && openComposeParam === "true") {
			// Find the contest index by name in the contests array
			const contestIndex = contests.findIndex(
				(contest) => contest.toLowerCase() === contestNameParam.toLowerCase()
			);

			if (contestIndex !== -1) {
				// Convert index to contest ID
				const contestID = getContestIDFromIndexLocal(contestIndex);
				// Set the contest filter to match the contest ID
				const newFilters = { ...filters, contestid: contestID };
				setFilters(newFilters);

				// Reload the feed with the new contest filter
				setLoading(true);
				newsfeed.getPosts(1, newFilters).then(() => {
					setLoading(false);
					// Open the compose modal after feed loads
					setShowCreatePost(true);
				});
			}
			// Clear URL params to avoid reopening on refresh
			window.history.replaceState({}, "", window.location.pathname);
		} else if (openComposeParam === "true") {
			// If only openCompose is present (without contestName), just open the modal
			// Reset selectedPostType to null so the post type selector shows first
			setSelectedPostType(null);
			setShowCreatePost(true);
			// Clear URL params to avoid reopening on refresh
			window.history.replaceState({}, "", window.location.pathname);
		}
	}, [contests]);

	// Prevent body scroll when modal is open (fixes iOS Safari scroll-through issue)
	useEffect(() => {
		if (showCreatePost) {
			// Save current scroll position
			const { scrollY } = window;

			// Lock body scroll
			document.body.style.position = "fixed";
			document.body.style.top = `-${scrollY}px`;
			document.body.style.left = "0";
			document.body.style.right = "0";
			document.body.style.width = "100%";
			document.body.style.overflow = "hidden";
			document.body.style.touchAction = "none";

			// Also lock html element
			document.documentElement.style.overflow = "hidden";
			document.documentElement.style.touchAction = "none";

			return () => {
				// Restore body scroll
				document.body.style.position = "";
				document.body.style.top = "";
				document.body.style.left = "";
				document.body.style.right = "";
				document.body.style.width = "";
				document.body.style.overflow = "";
				document.body.style.touchAction = "";

				// Restore html element
				document.documentElement.style.overflow = "";
				document.documentElement.style.touchAction = "";

				// Restore scroll position
				window.scrollTo(0, scrollY);
			};
		}
	}, [showCreatePost]);

	// Track if this is the initial mount
	const isInitialMount = useRef(true);

	// Loads initial data when component mounts
	useEffect(() => {
		// If on single post page
		if (showPost) {
			// If post was clicked, load related comments
			if (selectedPost) {
				const postItemId = getPostId(selectedPost);
				newsfeed.loadComments(postItemId);
				setLoading(false);
			} else {
				// If directly linked to post, get post and comments
				setLoading(true);
				handleGetPost(id, urlPostType);
			}
		} else {
			// If on newsfeed page, load first page of posts and set loading to false
			newsfeed.getPosts(1, filters).then(() => {
				setLoading(false);
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Only run on mount

	// Reload posts when filters change (but not on initial mount)
	useEffect(() => {
		// Skip initial mount and single post pages
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		if (showPost) {
			return;
		}

		setLoading(true);
		// Reset to page 1 when filters change - this will reset hasMorePosts properly
		newsfeed.getPosts(1, filters).then(() => {
			setLoading(false);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters.sort, filters.contestid, showPost]); // Only react to sort and contestid changes

	// Infinite scroll observer for loading more posts
	// Disable infinite scroll when post type filters are active (client-side filtering)
	const onLoadMore = useCallback(() => {
		// Use getState() to get fresh state at call time (avoid stale closure)
		const currentState = useNewsfeedStore.getState();
		if (
			!currentState.loadingPosts &&
			currentState.hasMorePosts &&
			!hasActiveFilters
		) {
			currentState.getPosts(currentState.postPageNum + 1, filters);
		}
	}, [hasActiveFilters, filters]);

	const lastItemRef = useInfiniteScroll({
		isLoading: loading,
		hasMore: hasActiveFilters ? false : newsfeed.hasMorePosts, // Disable when client-side filters are active
		onLoadMore,
	});

	// Gets one post and related comments by post id
	const handleGetPost = async (postId, postType = null) => {
		try {
			// Use provided postType or urlPostType from route params
			const typeToUse = postType || urlPostType;
			const response = await newsfeedApi.getPost(postId, typeToUse);
			// API returns response.data.result.post
			const fetchedPost =
				response.data?.result?.post ||
				response.result?.post ||
				response.data?.post;

			if (!fetchedPost) {
				setLoading(false);
				return;
			}

			setSelectedPost(fetchedPost);
			// Use the correct ID from the fetched post
			const postItemId = getPostId(fetchedPost) || postId;
			await newsfeed.loadComments(postItemId);
			setLoading(false);
		} catch (err) {
			console.error("Error in handleGetPost:", err);
			setLoading(false);
		}
	};

	// Handle selecting a post to display
	const onPostSelected = (item) => {
		setSelectedPost(item);
		// Use the correct ID based on post type
		const postItemId = getPostId(item);
		newsfeed.loadComments(postItemId);
	};

	// Handle filters change from NewsfeedFilters component
	const handleFiltersChange = (newFilters) => {
		setFilters(newFilters);
	};

	// Handle contest change callback
	const handleContestChange = (contestID) => {
		setFilters({ ...filters, contestid: contestID });
	};

	// Error boundary check
	if (!newsfeed) {
		return (
			<div className="mx-auto w-full max-w-xl p-4">
				<div className="text-red-500">
					Error: Newsfeed store not initialized
				</div>
			</div>
		);
	}
	if (!userStore) {
		return (
			<div className="mx-auto w-full max-w-xl p-4">
				<div className="text-red-500">Error: User store not initialized</div>
			</div>
		);
	}

	// Scroll to top handler
	const scrollToTop = () => {
		const scrollContainer = document.querySelector(".overflow-auto");
		if (scrollContainer) {
			scrollContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" });
		} else {
			window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
		}
	};

	if (!showPost) {
		return (
			<div className="min-h-screen w-full overflow-x-hidden bg-gray-50 dark:bg-gray-900">
				<Toaster position="top-center" richColors />
				<div ref={topOfFeedRef} className="relative w-full p-2 lg:p-4">
					{/* Desktop Sidebar - Fixed to left of centered content */}
					<div className="fixed left-4 top-20 z-30 hidden max-h-[calc(100vh-6rem)] w-64 space-y-4 overflow-y-auto xl:block">
						<PostTypeFilterSidebar
							postTypes={postTypes}
							postTypeFilters={postTypeFilters}
							togglePostTypeFilter={togglePostTypeFilter}
							clearPostTypeFilters={clearPostTypeFilters}
							hasActiveFilters={hasActiveFilters}
						/>
						<NewsfeedFilters
							sortOptions={sortOptions}
							filters={filters}
							onFiltersChange={handleFiltersChange}
							defaultFilters={defaultFilters}
							contests={contests}
							showComments={showComments}
							onShowCommentsChange={setShowComments}
							getContestIndexFromIDLocal={getContestIndexFromIDLocal}
							getContestIDFromIndexLocal={getContestIDFromIndexLocal}
							onContestChange={handleContestChange}
							userStore={userStore}
							userAuthAPI={userAuthAPI}
						/>
					</div>

					{/* Main Content */}
					<div className="mx-auto w-full min-w-0 max-w-lg flex-1">
						{/* Mobile Only - Full stack with spacing */}
						<div className="space-y-4 xl:hidden">
							{/* Create Post Button - Mobile (above filters) */}
							<button
								onClick={() => {
									setSelectedPostType(null); // Reset post type so selector shows
									setShowCreatePost(true);
								}}
								className="flex w-full items-center justify-center gap-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-4 shadow-lg transition-colors hover:from-purple-700 hover:to-pink-700"
							>
								<Plus size={28} className="text-white" />
								<span className="text-lg font-bold text-white">
									Create Post
								</span>
							</button>
							<MobilePostTypeFilters
								postTypes={postTypes}
								postTypeFilters={postTypeFilters}
								togglePostTypeFilter={togglePostTypeFilter}
								clearPostTypeFilters={clearPostTypeFilters}
								hasActiveFilters={hasActiveFilters}
							/>
							<NewsfeedFilters
								sortOptions={sortOptions}
								filters={filters}
								onFiltersChange={handleFiltersChange}
								defaultFilters={defaultFilters}
								contests={contests}
								showComments={showComments}
								onShowCommentsChange={setShowComments}
								getContestIndexFromIDLocal={getContestIndexFromIDLocal}
								getContestIDFromIndexLocal={getContestIDFromIndexLocal}
								onContestChange={handleContestChange}
								userStore={userStore}
								userAuthAPI={userAuthAPI}
								isMobile
							/>
						</div>

						{/* Posts section - Both mobile and desktop */}
						<div className="mt-4 min-w-0 space-y-4">
							{/* Create Post Button - Desktop (above contest switcher, only when sidebar is shown) */}
							<button
								onClick={() => {
									setSelectedPostType(null); // Reset post type so selector shows
									setShowCreatePost(true);
								}}
								className="hidden w-full items-center justify-center gap-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-4 shadow-lg transition-colors hover:from-purple-700 hover:to-pink-700 xl:flex"
							>
								<Plus size={28} className="text-white" />
								<span className="text-lg font-bold text-white">
									Create Post
								</span>
							</button>
							{/* Contest Switcher */}
							<ContestSwitcher
								contests={contests}
								currentContestId={filters.contestid}
								onContestChange={handleContestChange}
								getContestIndexFromIDLocal={getContestIndexFromIDLocal}
								getContestIDFromIndexLocal={getContestIDFromIndexLocal}
								userStore={userStore}
							/>

							{/* Post list */}
							<div className="space-y-4">
								{loading && filteredPosts.length === 0 ? (
									// Show skeleton loaders when initially loading
									Array.from({ length: 5 }).map((_, i) => (
										<PostSkeleton key={i} />
									))
								) : (
									<>
										{filteredPosts.map((item, index) => {
											// Calculate if this post is on the first page (page size is 10)
											const postPageSize = 10;
											const isFirstPage = index < postPageSize;

											return (
												<PostItem
													key={getPostUniqueId(item)}
													item={item}
													lastItemRef={null}
													isCompact
													onPostSelected={onPostSelected}
													showComments={showComments}
													currentContestFilter={filters.contestid}
													selectedCommentsPost={selectedCommentsPost}
													setSelectedCommentsPost={setSelectedCommentsPost}
													onSendLumens={() => {
														const itemId =
															item.GlowID || item.PrideID || item.GratitudeID;
														setSelectedPostForLumens(itemId);
														setShowSendLumens(true);
													}}
													loadAudioImmediately={isFirstPage}
												/>
											);
										})}

										{/* Loading more indicator */}
										{loading && filteredPosts.length > 0 ? (
											<div className="space-y-3">
												{Array.from({ length: 2 }).map((_, i) => (
													<PostSkeleton key={i} />
												))}
											</div>
										) : null}

										{/* Last item reference for intersection observer - only if no client-side filters */}
										{newsfeed.hasMorePosts && !loading && !hasActiveFilters ? (
											<div ref={lastItemRef} className="h-1" />
										) : null}

										{/* Back to Top - Always show */}
										<button
											type="button"
											onClick={scrollToTop}
											className="mb-[300px] flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-center text-white shadow-lg transition-colors hover:from-purple-700 hover:to-pink-700 md:mb-64"
										>
											<ArrowUp size={20} />
											<span>Back to Top</span>
										</button>
									</>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Send Lumens Modal */}
				<SendLumensModal
					isOpen={showSendLumens}
					onClose={() => {
						setShowSendLumens(false);
						setSelectedPostForLumens(null);
					}}
					selectedPostId={selectedPostForLumens}
					lumensToSend={lumensToSend}
					onSendLumens={async (postId, amount, memberId) => {
						return await newsfeed.handleSendLumens(postId, amount, memberId);
					}}
					userStore={userStore}
					newsfeed={newsfeed}
				/>

				{/* Create Post Modal */}
				{showCreatePost ? (
					<div
						className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 dark:bg-black/50 sm:p-4"
						style={{
							overflow: "hidden",
							touchAction: "none",
							WebkitOverflowScrolling: "touch",
						}}
						onTouchStart={(e) => {
							// Prevent all touch events from reaching background
							if (e.target === e.currentTarget) {
								e.preventDefault();
								e.stopPropagation();
							}
						}}
						onTouchMove={(e) => {
							// Prevent touch events from reaching background
							if (e.target === e.currentTarget) {
								e.preventDefault();
								e.stopPropagation();
							}
						}}
					>
						<div
							className="my-auto w-full max-w-xl overflow-y-auto"
							style={{
								maxHeight: "100vh",
								touchAction: "pan-y",
								pointerEvents: "auto",
								WebkitOverflowScrolling: "touch",
							}}
							onTouchStart={(e) => {
								// Stop propagation to prevent background scroll
								e.stopPropagation();
							}}
							onTouchMove={(e) => {
								// Allow scrolling within modal, but prevent background scroll
								e.stopPropagation();
							}}
						>
							<ComposePost
								onContestChange={handleContestChange}
								currentContestId={filters.contestid}
								selectedPostType={selectedPostType}
								onPostTypeSelect={setSelectedPostType}
								borderColor={
									selectedPostType === "grateful"
										? "border-green-500"
										: selectedPostType === "pride"
											? "border-purple-500"
											: "border-yellow-500"
								}
								onPostCreated={() => setShowCreatePost(false)}
								onClose={() => setShowCreatePost(false)}
							/>
						</div>
					</div>
				) : null}

				{/* Invite Confirmation Modal */}
				<InviteConfirmationModal
					isOpen={shouldShowInviteModal}
					onClose={() => setShowModal(false)}
					inviteData={inviteData}
					isLoading={isProcessing}
				/>

				{/* Fixed Floating Create Post Button - Bottom Right */}
				<button
					onClick={() => {
						setSelectedPostType(null); // Reset post type so selector shows
						setShowCreatePost(true);
					}}
					className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-pink-600 p-0 shadow-2xl transition-all hover:scale-110 hover:from-purple-700 hover:to-pink-700 active:scale-95 sm:bottom-8 sm:right-8 sm:h-16 sm:w-16"
					aria-label="Create Post"
				>
					<Plus size={28} className="text-white sm:h-8 sm:w-8" />
				</button>
			</div>
		);

		// Single post display page
	} else if (selectedPost) {
		return (
			<div className="relative mx-auto h-full w-full max-w-xl bg-background p-4 dark:bg-background">
				<Button
					className="mb-2 border-border bg-background text-foreground hover:bg-muted/50 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted/50"
					variant="outline"
					onClick={() => {
						navigate("/newsfeed");
						newsfeed.getPosts(1, filters);
					}}
				>
					Back
				</Button>
				<PostItem
					item={selectedPost}
					isCompact={false}
					onPostSelected={onPostSelected}
					currentContestFilter={filters.contestid}
				/>
			</div>
		);
	}

	// Loading state for focused post view
	return (
		<div className="relative mx-auto h-full w-full max-w-xl bg-background p-4 dark:bg-background">
			<Button
				className="mb-2 border-border bg-background text-foreground hover:bg-muted/50 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted/50"
				variant="outline"
				onClick={() => {
					navigate("/newsfeed");
					newsfeed.getPosts(1, filters);
				}}
			>
				Back
			</Button>
			{loading ? (
				<PostSkeleton />
			) : (
				<div className="mx-auto w-full max-w-xl p-4 text-foreground dark:text-foreground">
					Post not found
				</div>
			)}
		</div>
	);
};

export default Newsfeed;
